import { Check, CircleAlert, Minus } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Checkbox as CheckboxPrimitive } from 'radix-ui';
import { type ComponentProps, type ReactNode, useId } from 'react';

import { cn } from '../lib/cn.ts';
import { collapse } from '../lib/motion.tsx';

type CheckedState = boolean | 'indeterminate';

export interface CheckboxProps extends Omit<
  ComponentProps<typeof CheckboxPrimitive.Root>,
  'checked' | 'onCheckedChange' | 'children'
> {
  readonly checked: CheckedState;
  readonly onCheckedChange: (checked: boolean) => void;
}

/** Флажок: 16 px, отмеченный — акцентный; «частично» — для выбора группы строк. */
export function Checkbox({ checked, onCheckedChange, className, ...props }: CheckboxProps) {
  return (
    <CheckboxPrimitive.Root
      checked={checked}
      onCheckedChange={(value) => {
        onCheckedChange(value === true);
      }}
      className={cn(
        'peer inline-flex size-4 shrink-0 items-center justify-center rounded-xs border-thick border-muted bg-surface',
        'transition-colors hover:border-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
        'focus-visible:ring-offset-1 focus-visible:ring-offset-surface',
        'data-[state=checked]:border-accent data-[state=checked]:bg-accent data-[state=checked]:text-on-accent',
        'data-[state=indeterminate]:border-strong data-[state=indeterminate]:bg-strong data-[state=indeterminate]:text-on-strong',
        'disabled:cursor-not-allowed disabled:opacity-45',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex animate-pop-in items-center justify-center">
        {checked === 'indeterminate' ? (
          <Minus className="size-3" strokeWidth={3} aria-hidden="true" />
        ) : (
          <Check className="size-3" strokeWidth={3} aria-hidden="true" />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

/** Флажок с подписью справа и пояснением под ней; вся подпись нажимается. */
export function CheckboxField({
  label,
  description,
  error,
  checked,
  onCheckedChange,
  disabled,
  className,
}: {
  label: ReactNode;
  description?: ReactNode;
  /** Ошибка на русском под подписью: «Нужно согласие клиента». */
  error?: string | null | undefined;
  checked: CheckedState;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  const described = [description ? `${id}-description` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cn('flex items-start gap-2', className)}>
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className={cn('mt-0.5', error && 'border-danger')}
        aria-describedby={described === '' ? undefined : described}
        aria-invalid={error ? true : undefined}
      />
      <div className="flex min-w-0 flex-col">
        <label
          htmlFor={id}
          className={cn(
            'cursor-pointer text-body font-semibold',
            disabled && 'cursor-not-allowed text-muted',
          )}
        >
          {label}
        </label>
        {description ? (
          <p id={`${id}-description`} className="text-caption text-muted">
            {description}
          </p>
        ) : null}
        <FieldErrorNote id={`${id}-error`}>{error}</FieldErrorNote>
      </div>
    </div>
  );
}

/** Строка ошибки под флажком или переключателем. */
export function FieldError({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p id={id} className="flex items-start gap-1 text-caption font-semibold text-danger">
      <CircleAlert className="mt-px size-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

/** Зазор колонки, в которой стоит строка, и его компенсация: `-mt` у обёртки, `pt` внутри. */
const NOTE_GAP = { 0: ['', ''], 1: ['-mt-1', 'pt-1'], 2: ['-mt-2', 'pt-2'] } as const;

/**
 * `FieldError`, который раскрывается и схлопывается: появившаяся ошибка не сдвигает форму ниже за один кадр.
 * Пустое содержимое — строки нет. `gap` — зазор колонки, в которой она стоит (Tailwind-шаг): он переходит
 * внутрь строки, и у схлопнутой строки лишнего отступа не остаётся.
 */
export function FieldErrorNote({
  id,
  gap = 0,
  children,
}: {
  id: string;
  gap?: keyof typeof NOTE_GAP;
  children: ReactNode;
}) {
  const [outer, inner] = NOTE_GAP[gap];
  return (
    <AnimatePresence initial={false}>
      {children ? (
        <motion.div key="error" {...collapse} className={outer}>
          <div className={inner}>
            <FieldError id={id}>{children}</FieldError>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
